import { useState, type ReactNode } from 'react';
import {
  ArrowLeft,
  Award,
  CreditCard,
  FileCheck2,
  FileText,
  Mail,
  MapPin,
  MessageSquare,
  PencilLine,
  Phone,
  Trash2,
  ShieldCheck,
  UserCheck,
  Users,
} from 'lucide-react';
import type { StaffDetails, StaffMember } from '@/types/staff';
import type { InstituteRole } from '@/types/role';
import { BLOOD_GROUP_LABEL, GENDER_LABEL } from '@/constants/institute';
import type { Permission } from '@/constants/permissions';
import { useApiQuery } from '@/hooks/useApiQuery';
import { formatDate, formatTimeRange } from '@/lib/format';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { QueryError } from '@/components/ui/QueryError';
import { coverageOf } from '@/features/institute/roles/components/RoleAccessMeter';
import { staffAttendanceApi } from '@/features/institute/staffAttendance/api/staffAttendance.api';
import {
  EMPLOYMENT_TYPE_LABEL,
  MARITAL_STATUS_LABEL,
  STAFF_STATUS_LABEL,
  STAFF_STATUS_TONE,
} from '../constants';
import { cn, getNameInitials } from '@/lib/utils';
import { ROLE_LABEL } from '@/constants/roles';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SecureImage } from '@/components/ui/SecureImage';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { staffApi } from '../api/staff.api';
import { StaffAttendanceTab } from './StaffAttendanceTab';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { payrollApi } from '@/features/institute/payroll/api/payroll.api';
import { SalaryOverviewCard } from '@/features/institute/payroll/components/SalaryOverviewCard';
import { StaffSalaryPanel } from '@/features/institute/payroll/components/StaffSalaryPanel';
import { StaffLeaveTab } from './StaffLeaveTab';
import { StaffProfileActions } from './StaffProfileActions';

export type StaffProfileTab =
  | 'Overview'
  | 'Role'
  | 'Contact & Address'
  | 'Employment'
  | 'Payroll'
  | 'Documents'
  | 'Attendance'
  | 'Leave'
  | 'Performance'
  | 'Notes';

interface StaffProfilePageProps {
  member: StaffMember;
  onBack: () => void;
  onEdit: (member: StaffMember) => void;
  /** `STAFF_MANAGE` — gates the writes in "More actions". */
  canManage: boolean;
  /** Raised with the saved record when an action changes it, e.g. its status. */
  onChanged: (member: StaffMember) => void;
  /** The tab to open on — a link from the punch-ins register lands on Attendance. */
  initialTab?: StaffProfileTab;
  backLabel?: string;
  /**
   * The institute's roles, as the staff list already reads them — how a
   * colleague's role is turned into what it lets them do. Not needed for the
   * signed-in person's own profile, whose permissions come with their session.
   */
  roles?: InstituteRole[];
}

/**
 * Shown for every field the staff record does not hold yet. These used to be
 * sample values, which read as the person's own data — a made-up Aadhaar or
 * bank account on a real profile is worse than an honest blank.
 */
function NotProvided() {
  return <span className="font-medium text-ink-3">—</span>;
}

/** A recorded value, or the honest blank. */
function Shown({ value, mono = false }: { value: ReactNode; mono?: boolean }) {
  if (value === null || value === undefined || value === '') return <NotProvided />;
  return (
    <span className={cn('font-bold text-slate-900 dark:text-slate-100 text-right', mono && 'font-mono')}>
      {value}
    </span>
  );
}

export function StaffProfilePage({
  member,
  onBack,
  onEdit,
  canManage,
  onChanged,
  initialTab = 'Overview',
  backLabel = 'Staff',
  roles,
}: StaffProfilePageProps) {
  const [activeTab, setActiveTab] = useState<StaffProfileTab>(initialTab);

  /*
    The profile's details come with the full record only — the list row the
    profile may have been opened from leaves them out. Read again whenever the
    record changes (a save hands back a new one), and taken straight from it
    when it already carries them.
  */
  const detailsQuery = useApiQuery<StaffDetails | null>(
    () =>
      member.details !== undefined
        ? Promise.resolve(member.details)
        : staffApi.get(member.uuid).then((full) => full.details ?? null),
    [member],
  );
  const details = detailsQuery.data;

  /*
    The salary, read once and shared by the Overview card and the Payroll tab.
    Only for a viewer holding PAYROLL_VIEW — anyone else is never sent it — and
    never for the administrator account, which has no staff record to pay.
  */
  const permissions = useAuthStore((state) => state.user?.permissions);
  const signedInUuid = useAuthStore((state) => state.user?.uuid);
  const schoolFeatures = useAuthStore((state) => state.user?.features);
  const featureNames = useAuthStore((state) => state.user?.featureNames);
  const isSelf = member.uuid === signedInUuid;
  const canViewPayroll = Boolean(permissions?.includes('PAYROLL_VIEW'));
  const canManagePayroll = Boolean(permissions?.includes('PAYROLL_MANAGE'));
  const salaryQuery = useApiQuery(
    () =>
      canViewPayroll && !member.isOwner
        ? payrollApi.getStaffSalary(member.uuid)
        : Promise.resolve(null),
    [member.uuid, member.isOwner, canViewPayroll],
  );

  /*
    The school's opening and closing time — the hours staff keep, as the
    punch-in register measures them. Nobody's own shift is recorded, and the
    weekly pattern is not part of this read, so no days are claimed.
  */
  const canViewTimings = Boolean(permissions?.includes('STAFF_ATTENDANCE_VIEW'));
  const timingsQuery = useApiQuery(
    () => (canViewTimings ? staffAttendanceApi.timings() : Promise.resolve(null)),
    [canViewTimings],
  );
  function schoolHours() {
    if (timingsQuery.isLoading) return <span className="text-ink-3" aria-label="Loading">…</span>;
    const timings = timingsQuery.data;
    return <Shown value={timings ? formatTimeRange(timings.startsAt, timings.endsAt) : null} />;
  }

  /*
    What this person may do. The owner holds everything by definition; the
    signed-in person's own keys come with their session; anyone else's are their
    role's. Null when none of those is known — the role's name is shown alone
    rather than a guessed list.
  */
  const heldPermissions: Permission[] | null = member.isOwner
    ? null
    : isSelf
      ? (permissions ?? null)
      : (roles?.find((role) => role.uuid === member.role?.uuid)?.permissions ?? null);
  const coverage =
    member.isOwner || heldPermissions
      ? coverageOf(heldPermissions ?? [], schoolFeatures, featureNames).map((group) =>
          member.isOwner ? { ...group, held: group.total } : group,
        )
      : null;
  const reachedAreas = coverage?.filter((group) => group.held > 0) ?? [];
  const accessLevel = !coverage
    ? null
    : coverage.every((group) => group.held === group.total)
      ? 'Full access'
      : `${reachedAreas.length} of ${coverage.length} areas`;

  /** One field of the details: a quiet ellipsis while they load, never a premature "Not provided". */
  function detail(value: ReactNode, mono = false) {
    if (detailsQuery.isLoading) return <span className="text-ink-3" aria-label="Loading">…</span>;
    return <Shown value={value} mono={mono} />;
  }

  const emergencyContact =
    details?.emergencyContactName || details?.emergencyContactMobile
      ? [details.emergencyContactName, details.emergencyContactMobile].filter(Boolean).join(' · ')
      : null;

  const [confirmingPhotoRemoval, setConfirmingPhotoRemoval] = useState(false);
  const [removingPhoto, setRemovingPhoto] = useState(false);
  // The owner's account is support's to change; the server refuses it too.
  const canRemovePhoto = canManage && !member.isOwner && member.hasPhoto;

  async function removePhoto() {
    setRemovingPhoto(true);
    try {
      const saved = await staffApi.removePhoto(member.uuid);
      setConfirmingPhotoRemoval(false);
      toast.success('Photograph removed', `${member.fullName}'s initials will show in its place.`);
      onChanged(saved);
    } catch (caught) {
      toast.error(
        'Could not remove the photograph',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setRemovingPhoto(false);
    }
  }

  const initials = getNameInitials(member.fullName);
  const roleName = member.role?.name || ROLE_LABEL[member.staffType] || 'Staff Member';

  // Only the recorded joining date — the account's creation is not when they joined.
  const joiningDate = member.joinedOn ? formatDate(member.joinedOn) : null;

  const tabs = [
    'Overview',
    'Role',
    'Contact & Address',
    'Employment',
    'Payroll',
    'Documents',
    'Attendance',
    'Leave',
    'Performance',
    'Notes',
  ] as const;

  return (
    <div className="w-full space-y-3.5 pb-14 transition-colors">
      {/* Top Breadcrumb Navigation Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm font-medium">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-slate-500 hover:text-indigo-600 hover:underline dark:text-slate-400 dark:hover:text-indigo-400 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{backLabel}</span>
          </button>
          <span className="text-slate-300 dark:text-slate-600 font-normal">/</span>
          <span className="text-slate-900 dark:text-white font-bold">Staff profile</span>
        </div>
      </div>

      {/* Main Top Profile Header Card */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 sm:p-5 shadow-xs transition-all">
        <div className="flex flex-col xl:flex-row items-start justify-between gap-5">
          {/* Left Column: Avatar & Primary Details */}
          <div className="flex flex-col sm:flex-row items-start gap-4 min-w-0 w-full xl:w-auto">
            {/* Avatar */}
            <div className="relative shrink-0">
              <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 text-white flex items-center justify-center font-black text-2xl sm:text-3xl overflow-hidden border-4 border-slate-200 dark:border-slate-800 shadow-md">
                {member.hasPhoto ? (
                  <SecureImage
                    path={staffApi.photoPath(member.uuid)}
                    alt={member.fullName}
                    className="w-full h-full object-cover"
                    fallback={initials}
                  />
                ) : (
                  initials
                )}
              </div>
              {canRemovePhoto && (
                <Button
                  variant="dangerSoft"
                  size="sm"
                  className="mt-2 w-full"
                  leftIcon={<Trash2 className="w-3.5 h-3.5" aria-hidden="true" />}
                  onClick={() => setConfirmingPhotoRemoval(true)}
                >
                  Remove photo
                </Button>
              )}
            </div>

            {/* Name, Status & Contact Details */}
            <div className="min-w-0 space-y-1.5 pt-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight">
                  {member.fullName}
                </h1>
                {isSelf && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-extrabold bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300 uppercase tracking-wider">
                    YOU
                  </span>
                )}
              </div>

              <div className="text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-300">
                {roleName}
              </div>

              <div className="pt-0.5">
                {member.invitePending ? (
                  <Badge tone="warning">Invite pending</Badge>
                ) : (
                  <Badge tone={STAFF_STATUS_TONE[member.status]}>{STAFF_STATUS_LABEL[member.status]}</Badge>
                )}
              </div>

              {/* Phone & Email */}
              <div className="flex items-center gap-4 text-xs sm:text-sm font-semibold text-slate-700 dark:text-slate-300 pt-0.5 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-slate-400" />
                  <span>{member.mobile || <NotProvided />}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <span>{member.email}</span>
                </div>
              </div>

              {/* Employee Code & Date of Joining */}
              <div className="flex items-center gap-6 text-xs pt-2 mt-1 border-t border-slate-100 dark:border-slate-800/80">
                <div>
                  <span className="text-slate-400 block text-[11px] font-bold uppercase tracking-wider">Employee Code</span>
                  <span className="font-mono font-extrabold text-slate-900 dark:text-white">
                    {member.employeeCode || <NotProvided />}
                  </span>
                </div>
                <div>
                  <span className="text-slate-400 block text-[11px] font-bold uppercase tracking-wider">Date of Joining</span>
                  <span className="font-extrabold text-slate-900 dark:text-white">
                    {joiningDate ?? <NotProvided />}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Middle/Right 2-Column Metadata Section */}
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-2.5 text-xs sm:text-sm border-t xl:border-t-0 xl:border-l border-slate-200/80 dark:border-slate-800 pt-4 xl:pt-0 xl:pl-6 w-full xl:w-auto">
            {/* Column 1 */}
            <div className="space-y-2.5 mt-auto">
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">Date of Birth</span>
                {detail(details?.dateOfBirth ? formatDate(details.dateOfBirth) : null)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">Gender</span>
                {detail(details?.gender ? GENDER_LABEL[details.gender] : null)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">Blood Group</span>
                {detail(details?.bloodGroup ? BLOOD_GROUP_LABEL[details.bloodGroup] : null)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">Marital Status</span>
                {detail(details?.maritalStatus ? MARITAL_STATUS_LABEL[details.maritalStatus] : null)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">Nationality</span>
                {detail(details?.nationality)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">Aadhaar No.</span>
                {detail(details?.aadhaarNumber, true)}
              </div>
            </div>

            {/* Column 2 (Buttons placed at the TOP of second column) */}
            <div className="space-y-2.5">
              {/* Action Buttons at the Top of Second Column */}
              <div className="flex items-center justify-start sm:justify-end gap-2 pb-0.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => onEdit(member)}
                  className="inline-flex items-center gap-1.5 px-3 py-1 border border-blue-200/80 dark:border-blue-800/80 bg-blue-50/80 dark:bg-blue-950/60 hover:bg-blue-100/90 dark:hover:bg-blue-900/80 text-blue-700 dark:text-blue-300 rounded-xl text-xs font-extrabold transition-all shadow-2xs cursor-pointer"
                >
                  <PencilLine className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                  <span>Edit profile</span>
                </button>
                <StaffProfileActions member={member} canManage={canManage} onChanged={onChanged} />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">PAN No.</span>
                {detail(details?.panNumber, true)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">UAN No.</span>
                {detail(details?.uanNumber, true)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">PF No.</span>
                {detail(details?.pfNumber, true)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">ESI No.</span>
                {detail(details?.esiNumber, true)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">Bank Account</span>
                {detail(details?.bankAccountNumber, true)}
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-400 font-semibold">IFSC Code</span>
                {detail(details?.bankIfsc, true)}
              </div>
            </div>
          </div>
        </div>
      </div>

      {detailsQuery.error && (
        <QueryError
          title="Could not load this person's details"
          error={detailsQuery.error}
          onRetry={detailsQuery.refetch}
        />
      )}

      {/* Tab Switcher: Full Bottom Baseline + Active Tab Bottom Highlight Line & Soft Transparent Fill */}
      <div className="border-b border-slate-200 dark:border-slate-800 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden pt-1">
        <div className="flex items-center gap-1 min-w-max -mb-px">
          {tabs.map((tab) => {
            const isActive = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={cn(
                  'relative px-4 py-2 text-base font-extrabold transition-all duration-200 ease-in-out cursor-pointer whitespace-nowrap select-none rounded-t-lg',
                  isActive
                    ? 'border-b-2 border-indigo-600 dark:border-indigo-400 text-indigo-600 dark:text-indigo-400 bg-indigo-50/80 dark:bg-indigo-950/60'
                    : 'border-b-2 border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-100/60 dark:hover:bg-slate-800/40'
                )}
              >
                {tab}
              </button>
            );
          })}
        </div>
      </div>

      {/* TAB 1: OVERVIEW */}
      {activeTab === 'Overview' && (
        <div className="space-y-3.5 pt-1.5">
          {/* Row 1: Role | Contact Information | Address */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3.5">
            {/* Card 1: Role */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <Users className="w-4.5 h-4.5 text-indigo-600 dark:text-indigo-400" />
                <span>Role</span>
              </div>
              <div className="space-y-2.5 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Role</span>
                  <span className="font-bold text-slate-900 dark:text-slate-100">{roleName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Reporting To</span>
                  {detail(details?.reportingTo?.fullName)}
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 font-semibold">Access Level</span>
                  <Shown value={accessLevel} />
                </div>
              </div>
            </div>

            {/* Card 2: Contact Information */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <Phone className="w-4.5 h-4.5 text-sky-600 dark:text-sky-400" />
                <span>Contact Information</span>
              </div>
              <div className="space-y-2.5 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Phone</span>
                  <span className="font-bold text-slate-900 dark:text-slate-100">{member.mobile || <NotProvided />}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Alternate Phone</span>
                  {detail(details?.alternateMobile, true)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Email</span>
                  <span className="font-bold text-slate-900 dark:text-slate-100 truncate max-w-[180px]">{member.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Emergency Contact</span>
                  {detail(emergencyContact)}
                </div>
              </div>
            </div>

            {/* Card 3: Address */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <MapPin className="w-4.5 h-4.5 text-amber-600 dark:text-amber-400" />
                <span>Address</span>
              </div>
              <div className="space-y-2.5 text-xs sm:text-sm">
                <div>
                  <span className="text-slate-400 block font-semibold">Present Address</span>
                  <span className="block whitespace-pre-line">{detail(details?.presentAddress)}</span>
                </div>
                <div className="pt-1">
                  <span className="text-slate-400 block font-semibold">Permanent Address</span>
                  <span className="block whitespace-pre-line">{detail(details?.permanentAddress)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Employment Details | Payroll Overview */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
            {/* Card 4: Employment Details */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <UserCheck className="w-4.5 h-4.5 text-teal-600 dark:text-teal-400" />
                <span>Employment Details</span>
              </div>
              <div className="grid grid-cols-2 gap-y-2.5 text-xs sm:text-sm">
                <div className="flex justify-between pr-4">
                  <span className="text-slate-400 font-semibold">Employee Code</span>
                  <span className="font-mono font-bold text-slate-900 dark:text-slate-100">{member.employeeCode || <NotProvided />}</span>
                </div>
                <div className="flex justify-between pl-4 border-l border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">Confirmation Date</span>
                  {detail(details?.confirmedOn ? formatDate(details.confirmedOn) : null)}
                </div>
                <div className="flex justify-between pr-4">
                  <span className="text-slate-400 font-semibold">Date of Joining</span>
                  <Shown value={joiningDate} />
                </div>
                <div className="flex justify-between pl-4 border-l border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">Notice Period</span>
                  {detail(details?.noticePeriodDays != null ? `${details.noticePeriodDays} days` : null)}
                </div>
                <div className="flex justify-between pr-4">
                  <span className="text-slate-400 font-semibold">Employment Type</span>
                  {detail(details?.employmentType ? EMPLOYMENT_TYPE_LABEL[details.employmentType] : null)}
                </div>
                <div className="flex justify-between pl-4 border-l border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">Work Location</span>
                  {detail(details?.workLocation)}
                </div>
                <div className="flex justify-between pr-4">
                  <span className="text-slate-400 font-semibold">Probation ends</span>
                  {detail(details?.probationEndsOn ? formatDate(details.probationEndsOn) : null)}
                </div>
                <div className="flex justify-between pl-4 border-l border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">School hours</span>
                  {schoolHours()}
                </div>
              </div>
            </div>

            {/* Card 5: Salary */}
            <SalaryOverviewCard
              staffUuid={member.uuid}
              salary={salaryQuery}
              canView={canViewPayroll}
              isOwner={member.isOwner}
            />
          </div>

          {/* Row 3: Bank Details | Additional Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3.5">
            {/* Card 6: Bank Details */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <CreditCard className="w-4.5 h-4.5 text-violet-600 dark:text-violet-400" />
                <span>Bank Details</span>
              </div>
              <div className="grid grid-cols-2 gap-y-2.5 text-xs sm:text-sm">
                <div className="flex justify-between pr-4">
                  <span className="text-slate-400 font-semibold">Bank Name</span>
                  {detail(details?.bankName)}
                </div>
                <div className="flex justify-between pl-4 border-l border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">IFSC Code</span>
                  {detail(details?.bankIfsc, true)}
                </div>
                <div className="flex justify-between pr-4">
                  <span className="text-slate-400 font-semibold">Account Number</span>
                  {detail(details?.bankAccountNumber, true)}
                </div>
                <div className="flex justify-between pl-4 border-l border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">Account Holder Name</span>
                  {detail(details?.bankAccountHolder)}
                </div>
              </div>
            </div>

            {/* Card 7: Additional Information */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <FileText className="w-4.5 h-4.5 text-cyan-600 dark:text-cyan-400" />
                <span>Additional Information</span>
              </div>
              <div className="grid grid-cols-2 gap-y-2.5 text-xs sm:text-sm">
                <div className="flex justify-between pr-4">
                  <span className="text-slate-400 font-semibold">Qualification</span>
                  {detail(details?.qualification)}
                </div>
                <div className="flex justify-between pl-4 border-l border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">Languages Known</span>
                  {detail(details?.languagesKnown)}
                </div>
                <div className="flex justify-between pr-4">
                  <span className="text-slate-400 font-semibold">Experience</span>
                  {detail(details?.experienceYears != null ? `${details.experienceYears} years` : null)}
                </div>
                <div className="flex justify-between pl-4 border-l border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">Passport No.</span>
                  {detail(details?.passportNumber, true)}
                </div>
                <div className="col-span-2 flex justify-between pt-1 border-t border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 font-semibold">Skills</span>
                  {detail(details?.skills)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 2: ROLE */}
      {activeTab === 'Role' && (
        <div className="space-y-3.5 pt-1.5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <Users className="w-4.5 h-4.5 text-indigo-600 dark:text-indigo-400" />
                <span>Primary Designation</span>
              </div>
              <div className="space-y-2.5 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Designation</span>
                  <Shown value={member.designation} />
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Staff Type</span>
                  <span className="font-bold text-slate-900 dark:text-slate-100">{ROLE_LABEL[member.staffType]}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Reporting Manager</span>
                  {detail(details?.reportingTo?.fullName)}
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <ShieldCheck className="w-4.5 h-4.5 text-emerald-600 dark:text-emerald-400" />
                <span>Access & Permissions Scope</span>
              </div>
              <div className="space-y-2.5 text-xs sm:text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 font-semibold">System Role</span>
                  <span className="px-2 py-0.5 rounded-md font-bold bg-indigo-100 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
                    {roleName}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400 font-semibold">Permission Level</span>
                  <Shown value={accessLevel} />
                </div>
                <div className="pt-1.5">
                  <span className="text-slate-400 block font-semibold mb-1">Areas they can reach</span>
                  {coverage === null ? (
                    <NotProvided />
                  ) : reachedAreas.length === 0 ? (
                    <span className="font-medium text-ink-3">None — this role grants no permissions</span>
                  ) : (
                    <ul className="flex flex-wrap gap-1.5">
                      {reachedAreas.map((area) => (
                        <li
                          key={area.title}
                          title={`${area.held} of ${area.total} permissions`}
                          className="px-2.5 py-0.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-lg text-xs font-bold border border-indigo-200/60 dark:border-indigo-800/60"
                        >
                          {area.title}
                          {area.held < area.total && (
                            <span className="font-medium"> · {area.held} of {area.total}</span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: CONTACT & ADDRESS */}
      {activeTab === 'Contact & Address' && (
        <div className="space-y-3.5 pt-1.5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <Phone className="w-4.5 h-4.5 text-sky-600 dark:text-sky-400" />
                <span>Contact Channels</span>
              </div>
              <div className="space-y-2.5 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Mobile Number</span>
                  <span className="font-bold text-slate-900 dark:text-slate-100">{member.mobile || <NotProvided />}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Alternate Phone</span>
                  {detail(details?.alternateMobile, true)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Official Email</span>
                  <span className="font-bold text-slate-900 dark:text-slate-100">{member.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Emergency Contact</span>
                  {detail(emergencyContact)}
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-3">
              <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
                <MapPin className="w-4.5 h-4.5 text-amber-600 dark:text-amber-400" />
                <span>Residential Address</span>
              </div>
              <div className="space-y-2.5 text-xs sm:text-sm">
                <div>
                  <span className="text-slate-400 block font-semibold">Present Address</span>
                  <span className="block whitespace-pre-line">{detail(details?.presentAddress)}</span>
                </div>
                <div className="pt-1.5 border-t border-slate-100 dark:border-slate-800">
                  <span className="text-slate-400 block font-semibold">Permanent Address</span>
                  <span className="block whitespace-pre-line">{detail(details?.permanentAddress)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 4: EMPLOYMENT */}
      {activeTab === 'Employment' && (
        <div className="space-y-3.5 pt-1.5">
          <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 shadow-xs space-y-4">
            <div className="flex items-center gap-2.5 text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800/80 pb-2.5">
              <UserCheck className="w-4.5 h-4.5 text-teal-600 dark:text-teal-400" />
              <span>Complete Employment Lifecycle & Status</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 text-xs sm:text-sm">
              <div className="space-y-2.5">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Employee Code</span>
                  <span className="font-mono font-bold text-slate-900 dark:text-slate-100">{member.employeeCode || <NotProvided />}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Employment Type</span>
                  {detail(details?.employmentType ? EMPLOYMENT_TYPE_LABEL[details.employmentType] : null)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Date of Joining</span>
                  <Shown value={joiningDate} />
                </div>
              </div>

              <div className="space-y-2.5 border-t md:border-t-0 md:border-l border-slate-100 dark:border-slate-800 pt-2.5 md:pt-0 md:pl-5">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Confirmation Date</span>
                  {detail(details?.confirmedOn ? formatDate(details.confirmedOn) : null)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Probation ends</span>
                  {detail(details?.probationEndsOn ? formatDate(details.probationEndsOn) : null)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Notice Period</span>
                  {detail(details?.noticePeriodDays != null ? `${details.noticePeriodDays} days` : null)}
                </div>
              </div>

              <div className="space-y-2.5 border-t md:border-t-0 md:border-l border-slate-100 dark:border-slate-800 pt-2.5 md:pt-0 md:pl-5">
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Work Location</span>
                  {detail(details?.workLocation)}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">School hours</span>
                  {schoolHours()}
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-400 font-semibold">Biometric ID</span>
                  {detail(details?.biometricId, true)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TAB 5: PAYROLL */}
      {activeTab === 'Payroll' && (
        <StaffSalaryPanel
          staffUuid={member.uuid}
          staffName={member.fullName}
          joinedOn={member.joinedOn}
          salary={salaryQuery}
          canView={canViewPayroll}
          canManage={canManagePayroll}
          isOwner={member.isOwner}
          defaultPaymentMode={member.details?.salaryPaymentMode ?? null}
        />
      )}

      {/* TAB 6: DOCUMENTS */}
      {activeTab === 'Documents' && (
        <NotRecordedYet
          icon={<FileCheck2 className="h-5 w-5" />}
          title="No documents recorded yet"
          description="Staff documents such as ID proofs, certificates and contracts cannot be uploaded yet, so none are on file for this person."
        />
      )}

      {/* TAB 7: ATTENDANCE */}
      {activeTab === 'Attendance' && <StaffAttendanceTab staffUuid={member.uuid} />}

      {/* TAB 8: LEAVE */}
      {activeTab === 'Leave' && (
        <StaffLeaveTab staffUuid={member.uuid} staffName={member.firstName} />
      )}

      {/* TAB 9: PERFORMANCE */}
      {activeTab === 'Performance' && (
        <NotRecordedYet
          icon={<Award className="h-5 w-5" />}
          title="No performance reviews recorded yet"
          description="Appraisals and ratings are not kept for staff yet, so there is nothing to show for this person."
        />
      )}

      {/* TAB 10: NOTES */}
      {activeTab === 'Notes' && (
        <NotRecordedYet
          icon={<MessageSquare className="h-5 w-5" />}
          title="No notes recorded yet"
          description="Internal notes about staff cannot be saved yet, so none are on file for this person."
        />
      )}

      <ConfirmDialog
        isOpen={confirmingPhotoRemoval}
        title="Remove this photograph?"
        description={`${member.fullName}'s photograph will be deleted and their initials shown instead. You can upload a new one from Edit.`}
        confirmLabel="Remove photo"
        tone="danger"
        isLoading={removingPhoto}
        onConfirm={removePhoto}
        onCancel={() => setConfirmingPhotoRemoval(false)}
      />
    </div>
  );
}

/**
 * A tab whose records the product does not keep yet. It says so plainly — these
 * tabs once held sample files, ratings and notes that read as this person's own.
 * The tab stays so the bar does not shift when the records arrive.
 */
function NotRecordedYet({ icon, title, description }: { icon: ReactNode; title: string; description: string }) {
  return (
    <div className="pt-1.5">
      <div className="card">
        <EmptyState icon={icon} title={title} description={description} />
      </div>
    </div>
  );
}
